import React, {useEffect, useRef, useState} from "react";
import {Box} from "@mui/material";

/**
 * ZigzagDivider
 *
 * topBg      – background / notch fill colour (top section)
 * color1     – first row of teeth  (optional)
 * color2     – second row of teeth (optional, requires color1)
 * bottomBg   – colour below the last teeth row (= next section bg)
 *
 * yOffset    – vertical px shift between successive teeth rows
 * toothWidth / toothHeight – fixed pixel dimensions of each tooth
 */
export const ZigzagDivider = ({
    topBg = '#509F33',
    color1,
    color2,
    bottomBg = '#FFFFFF',
    yOffset = 30,
    toothWidth = 75,
    toothHeight = 40,
}) => {
    const containerRef = useRef(null);
    const [width, setWidth] = useState(() =>
        typeof window !== 'undefined' ? window.innerWidth : 1600
    );

    useEffect(() => {
        if (!containerRef.current) return;
        const observer = new ResizeObserver(entries => {
            setWidth(entries[0].contentRect.width);
        });
        observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, []);

    // bottomBg always gets its own teeth row at the end
    const allRows = [...[color1, color2].filter(Boolean), bottomBg];
    const numTeeth = Math.ceil(width / toothWidth) + 1;
    const svgW = numTeeth * toothWidth;
    const bottomPad = toothHeight * 0.5;
    const svgH = toothHeight + (allRows.length - 1) * yOffset + bottomPad;

    // Filled polygon from the sawtooth at `baseY` down to svgH
    const makePath = (baseY) => {
        let d = `M0,${svgH} L${svgW},${svgH} L${svgW},${baseY}`;
        for (let i = numTeeth - 1; i >= 0; i--) {
            d += ` L${i * toothWidth + toothWidth / 2},${baseY + toothHeight} L${i * toothWidth},${baseY}`;
        }
        return d + ' Z';
    };

    return (
        <Box ref={containerRef} sx={{lineHeight: 0, background: bottomBg, overflow: 'hidden'}}>
            <svg
                viewBox={`0 0 ${svgW} ${svgH}`}
                width={svgW}
                height={svgH}
                style={{display: 'block'}}
                xmlns="http://www.w3.org/2000/svg"
            >
                <rect width={svgW} height={svgH} fill={topBg}/>

                {allRows.map((color, i) => (
                    <path key={i} d={makePath(i * yOffset)} fill={color}/>
                ))}
            </svg>
        </Box>
    );
};
